import React, { useState, useEffect } from 'react';
import { getLeagueStandings } from '../DatabaseAPI';
import { Button } from '../components/ui';
import './StandingsPage.css';

function StandingsPage({ user, setPage, leagueId }) {
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!leagueId) {
            setError('No league ID provided');
            setLoading(false);
            return;
        }

        getLeagueStandings(leagueId)
            .then(data => {
                if (data && Array.isArray(data)) {
                    // Sort teams by score (highest first)
                    const sortedTeams = data.sort((a, b) => (b.score || 0) - (a.score || 0));
                    setStandings(sortedTeams);
                } else {
                    setError('Invalid standings data received');
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading league standings:', error);
                setError('Failed to load league standings');
                setLoading(false);
            });
    }, [leagueId]);

    const handleNavigation = (destination) => {
        console.log('StandingsPage navigation:', destination);
        
        switch(destination) {
            case 'LEAGUES':
                setPage('HOME'); // Go back to leagues/teams selection
                break;
            case 'TEAM':
                setPage('TEAM');
                break;
            case 'STANDINGS':
                // Already on standings page
                break;
            case 'GEAR':
                setPage('GEAR');
                break;
            case 'LEADERBOARD':
                setPage('LEADERBOARD');
                break;
            default:
                console.log('Unknown navigation destination:', destination);
                break;
        }
    };

    const handleTeamClick = (team) => {
        // Compare team.id with user.id (both are team IDs)
        // user.id is a string, team.id is a number, so convert for comparison
        const isOwnTeam = team.id === parseInt(user?.id);
        
        if (isOwnTeam) {
            // Navigate to user's own team page
            // Clear ALL opponent-related state
            sessionStorage.removeItem('opponentTeamId');
            sessionStorage.removeItem('opponentTeamName');
            sessionStorage.removeItem('viewMode');
            sessionStorage.removeItem('playerViewMode');
            localStorage.removeItem('currentScreen');
            setPage('TEAM');
        } else {
            // Navigate to opponent team view
            sessionStorage.setItem('opponentTeamId', team.id);
            sessionStorage.setItem('opponentTeamName', team.display_name || team.name);
            sessionStorage.setItem('viewMode', 'opponent');
            setPage(2); // NEW_PAGES.CHARACTER constant for opponent team view
        }
    };

    const getRankSuffix = (rank) => {
        if (rank === 1) return 'ST';
        if (rank === 2) return 'ND';
        if (rank === 3) return 'RD';
        return 'TH';
    };

    if (loading) {
        return (
            <div className="standings-page-loading">
                <h2>Loading League Standings...</h2>
            </div>
        );
    }

    if (error) {
        return (
            <div className="standings-page-error">
                <h2>Error: {error}</h2>
                <Button onClick={() => setPage('TEAM')} variant="secondary">
                    Back to Team
                </Button>
            </div>
        );
    }

    return (
        <div className="standings-page-mobile">
            {/* Top Navigation Bar */}
            <div className="standings-top-nav-bar">
                <Button
                    className="standings-top-nav-button"
                    onClick={() => handleNavigation('LEAGUES')}
                    variant="secondary"
                >
                    LEAGUES
                </Button>
                <Button
                    className="standings-top-nav-button"
                    onClick={() => handleNavigation('TEAM')}
                    variant="secondary"
                >
                    TEAM
                </Button>
                <Button
                    className="standings-top-nav-button active"
                    onClick={() => handleNavigation('STANDINGS')}
                    variant="secondary"
                >
                    STANDINGS
                </Button>
                <Button
                    className="standings-top-nav-button"
                    onClick={() => handleNavigation('GEAR')}
                    variant="secondary"
                >
                    GEAR
                </Button>
            </div>

            {/* Standings List */}
            <div className="standings-list-container">
                {standings.length === 0 ? (
                    <div className="no-standings">No teams found in this league.</div>
                ) : (
                    standings.map((team, index) => {
                        const rank = index + 1;
                        // FIXED: Compare team.id with user.id (both are team IDs)
                        const isCurrentUser = team.id === parseInt(user?.id);
                        
                        return (
                            <div 
                                key={team.id}
                                className={`standings-card ${isCurrentUser ? 'current-user' : ''}`}
                                onClick={() => handleTeamClick(team)}
                            >
                                <div className="standings-card-left">
                                    <div className="rank-badge">
                                        <span className="rank-number">{rank}</span>
                                        <span className="rank-suffix">{getRankSuffix(rank)}</span>
                                    </div>
                                    <img 
                                        src="/art/TeamIcon.png" 
                                        alt="Team Icon" 
                                        className="standings-team-icon"
                                    />
                                </div>
                                <div className="standings-card-right">
                                    <div className="standings-team-name">
                                        {team.display_name || team.name || 'TEAM'}
                                    </div>
                                    <div className="standings-total-label">TOTAL SCORE</div>
                                    <div className="standings-total-score">
                                        {(team.score || 0).toFixed(1)}PT
                                    </div>
                                </div>
                                {rank <= 3 && (
                                    <div className="medal-icon">
                                        {rank === 1 && '🥇'}
                                        {rank === 2 && '🥈'}
                                        {rank === 3 && '🥉'}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default StandingsPage;
